import React from "react";
import {
  Card,
  Col,
  Row,
  Typography,
  Form,
  Input,
  Select,
  Flex,
  Switch,
  DatePicker,
  Button,
} from "antd";
import moment from "moment-timezone";
import currencyData from "../../../../../../../shared-components/currency.json";
import { countries } from "../../../../../../../shared-components/countries";
import DownloadDocIcon from "../../../../../../../Icons/DownloadDocIcon";
import { useSelector } from "react-redux";

const { Text } = Typography;
const { TextArea } = Input;

const currencyList = currencyData?.currencies || [];

const OverviewS1 = React.forwardRef((props, ref) => {
  const themeDefault = useSelector((state) => state.themeDefault);
  const isLightBg = themeDefault?.info?.themeColor === "default";
  const [form] = Form.useForm();
  const values = Form.useWatch([], form);

  const handleCountryChange = (value) => {
    // Clear currency, timezone, and language when country changes
    form.setFieldsValue({
      currency: undefined,
      timezone: undefined,
      language: undefined,
    });
  };

  React.useImperativeHandle(ref, () => ({
    getValues: () => ({
      ...(values || {}),
      ...(form.getFieldsValue(true) || {}),
    }),
    setValues: (newValues) => {
      form.setFieldsValue(newValues);
    },
    resetForm: () => {
      form.resetFields();
      form.setFieldsValue({
        entityId: "",
        entityName: "",
        description: "",
        enableDimensions: false,
        enableRegionalPerDimension: false,
        country: undefined,
        currency: undefined,
        timezone: undefined,
        language: undefined,
        enableFinancialConsolidation: false,
        enableEntityFilter: false,
        internalNotes: "",
      });
    },
  }));

  return (
    <Form
      form={form}
      layout="vertical"
      initialValues={{
        entityId: "",
        entityName: "",
        description: "",
        enableDimensions: false,
        enableRegionalPerDimension: false,
        country: undefined,
        currency: undefined,
        timezone: undefined,
        language: undefined,
        enableFinancialConsolidation: false,
        enableEntityFilter: false,
        internalNotes: "",
      }}
    >
      <Row gutter={[16, 16]}>
        <Col span={12}>
          <Card
            title={
              <div
                style={{
                  borderBottom: themeDefault?.info?.newConfig?.cardborder,
                  paddingBottom: 8,
                }}
              >
                General Settings
              </div>
            }
            bordered
          >
            <Flex gap={16}>
              <Form.Item name="entityId" label="Entity ID" style={{ flex: 1 }}>
                <Input placeholder="Entity Id" disabled />
              </Form.Item>
              <Form.Item
                name="entityName"
                label="Entity Name"
                style={{ flex: 1 }}
              >
                <Input placeholder="Enter Entity Name" />
              </Form.Item>
            </Flex>

            <Form.Item name="description" label="Description">
              <TextArea rows={2} placeholder="Enter description" />
            </Form.Item>
          </Card>

          <Card
            style={{ marginTop: 16 }}
            bordered
            title={
              <div
                style={{
                  borderBottom: themeDefault?.info?.newConfig?.cardborder,
                  paddingBottom: 8,
                }}
              >
                Dimensions
              </div>
            }
          >
            <Flex align="center" gap={8}>
              <Form.Item
                name="enableDimensions"
                valuePropName="checked"
                noStyle
              >
                <Switch />
              </Form.Item>
              <Text style={{ fontSize: 12, fontWeight: "bold" }}>
                Enable dimensions for this legal entity
              </Text>
            </Flex>
            <Text type="secondary" style={{ fontSize: 12, paddingTop: 4 }}>
              If enabled, the legal entity will have multiple dimensions
            </Text>
          </Card>

          {/* <Card
            style={{ marginTop: 16 }}
            bordered
            title={
              <div
                style={{
                  borderBottom: themeDefault?.info?.newConfig?.cardborder,
                  paddingBottom: 8,
                }}
              >
                File Attachments
              </div>
            }
          >
            <Flex vertical gap={8}>
              <Text style={{ fontSize: 16, fontWeight: 700 }}>
                Upload a file(s) to link with this legal entity
              </Text>
              <Text type="secondary" style={{ fontSize: 12, paddingTop: 4 }}>
                Upload the relevant files for the legal entity
              </Text>
              <Button style={{ width: 150 }}>
                <DownloadDocIcon size="small" />
                Upload File
              </Button>
              <Text type="secondary" style={{ fontSize: 12, paddingTop: 4 }}>
                Only .xlsx, .xls, .csv files are supported
              </Text>
            </Flex>
          </Card> */}
        </Col>

        <Col span={12}>
          <Card
            title={
              <Flex
                align="center"
                justify="space-between"
                style={{
                  borderBottom: themeDefault?.info?.newConfig?.cardborder,
                  paddingBottom: 8,
                }}
              >
                Regional Settings
                <Flex gap={4} align="center">
                  <Form.Item
                    name="enableRegionalPerDimension"
                    valuePropName="checked"
                    noStyle
                  >
                    <Switch />
                  </Form.Item>
                  <Text style={{ fontSize: 12, fontWeight: 700 }}>
                    Enable regional settings per dimension
                  </Text>
                </Flex>
              </Flex>
            }
            bordered
          >
            <Flex gap={16} wrap="wrap">
              <Flex vertical style={{ flex: 1, minWidth: 250 }}>
                <Form.Item
                  name="country"
                  label="Country"
                  style={{ padding: 0, margin: 0 }}
                >
                  <Select
                    placeholder="Select country"
                    showSearch
                    allowClear
                    filterOption={(input, option) =>
                      (option?.label ?? "")
                        .toLowerCase()
                        .includes(input.toLowerCase())
                    }
                    options={countries.map((c) => ({
                      value: c,
                      label: c,
                    }))}
                    onChange={handleCountryChange}
                  />
                </Form.Item>
                <Text type="secondary" style={{ fontSize: 12, paddingTop: 4 }}>
                  Select the country for the dimension
                </Text>
              </Flex>

              <Flex vertical style={{ flex: 1, minWidth: 250 }}>
                <Form.Item
                  name="currency"
                  label="Currency"
                  style={{ padding: 0, margin: 0 }}
                >
                  <Select
                    placeholder="Select currency"
                    showSearch
                    allowClear
                    filterOption={(input, option) =>
                      (option?.label ?? "")
                        .toLowerCase()
                        .includes(input.toLowerCase())
                    }
                    options={currencyList.map((c) => ({
                      value: c.symbol,
                      label: `${c.displayName} (${c.symbol})`,
                    }))}
                  />
                </Form.Item>
                <Text type="secondary" style={{ fontSize: 12, paddingTop: 4 }}>
                  Select the currency for the whole entity
                </Text>
              </Flex>
            </Flex>

            <Flex gap={16} wrap="wrap" style={{ marginTop: 8 }}>
              <Flex vertical style={{ flex: 1, minWidth: 250 }}>
                <Form.Item
                  name="timezone"
                  label="Timezone"
                  style={{ padding: 0, margin: 0 }}
                >
                  <Select
                    placeholder="Select timezone"
                    showSearch
                    allowClear
                    filterOption={(input, option) =>
                      (option?.label ?? "")
                        .toLowerCase()
                        .includes(input.toLowerCase())
                    }
                    options={moment.tz
                      .names()
                      .map((tz) => ({ value: tz, label: tz }))}
                  />
                </Form.Item>
                <Text type="secondary" style={{ fontSize: 12, paddingTop: 4 }}>
                  Select the timezone for the dimension
                </Text>
              </Flex>

              <Flex vertical style={{ flex: 1, minWidth: 250 }}>
                <Form.Item
                  name="language"
                  label="Language"
                  style={{ padding: 0, margin: 0 }}
                >
                  <Select
                    placeholder="Select Language"
                    showSearch
                    allowClear
                    filterOption={(input, option) =>
                      (option?.label ?? "")
                        .toLowerCase()
                        .includes(input.toLowerCase())
                    }
                    options={[
                      { value: "en", label: "English (EN)" },
                      { value: "zh", label: "Chinese (ZH)" },
                      { value: "ar", label: "Arabic (AR)" },
                    ]}
                  />
                </Form.Item>
                <Text type="secondary" style={{ fontSize: 12, paddingTop: 4 }}>
                  Select language for the dimension
                </Text>
              </Flex>
            </Flex>
          </Card>
          <Card
            title={
              <div
                style={{
                  borderBottom: themeDefault?.info?.newConfig?.cardborder,
                  paddingBottom: 8,
                }}
              >
                Dashboards & Reporting
              </div>
            }
            style={{ marginTop: 16 }}
            bordered
          >
            {/* Switch 1 */}
            <Flex align="center" gap={8} style={{ marginBottom: 6 }}>
              <Form.Item
                name="enableFinancialConsolidation"
                valuePropName="checked"
                noStyle
              >
                <Switch />
              </Form.Item>
              <Text style={{ fontSize: 12, fontWeight: "bold" }}>
                Use for Financial Consolidation process
              </Text>
            </Flex>
            <Text
              type="secondary"
              style={{
                fontSize: 12,
                marginTop: 0,
                marginBottom: 12,
                display: "block",
              }}
            >
              This dimension will have impact on financial reporting
            </Text>

            {/* Switch 2 */}
            <Flex align="center" gap={8} style={{ marginBottom: 6 }}>
              <Form.Item
                name="enableEntityFilter"
                valuePropName="checked"
                noStyle
              >
                <Switch />
              </Form.Item>
              <Text style={{ fontSize: 12, fontWeight: "bold" }}>
                Enable this entity as a filter across the web portal
              </Text>
            </Flex>
            <Text
              type="secondary"
              style={{
                fontSize: 12,
                marginTop: 0,
                marginBottom: 0,
                display: "block",
              }}
            >
              This will enable filtering portal as per the dimensions
            </Text>
          </Card>

          <Card
            title={
              <div
                style={{
                  borderBottom: themeDefault?.info?.newConfig?.cardborder,
                  paddingBottom: 8,
                }}
              >
                Internal Notes (For Internal Use Only)
              </div>
            }
            style={{ marginTop: 16 }}
            bordered
          >
            <Form.Item name="internalNotes" label="Notes">
              <TextArea rows={4} placeholder="Enter Notes" />
            </Form.Item>
          </Card>
        </Col>
      </Row>
    </Form>
  );
});

export default OverviewS1;
